import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Flex, List, MenuProps, Modal, Space } from "antd";
import { DownOutlined, PlusCircleFilled } from "@ant-design/icons";
import { debounce } from "lodash";
import { toast } from "react-toastify";

import { AppDispatch, RootState } from "../../../redux/store";
import { useSettingSidebarContext } from "../../../context/SettingSidebarScreenContext";
import { makeCall, setCallerId } from "../../../redux/slices/sipSlice";
import { useSocket } from "../../../context/SocketContext";
import {
  useGetBalanceInfoQuery,
  useGetClientRatesMutation,
  useGetPhoneNumbersQuery,
  useIsNumberRegisteredMutation,
} from "../../../redux/services/api";
import CustomLoader from "../../../components/common/UI/Loader";
import ContactCard from "../../../components/Chat/ContactCard";
import SearchInput from "../../../components/common/UI/SearchInput";
import Dialer from "../../../components/common/Dialer";
import Favourites from "../../../components/calls/Favourites";
import TopUpScreen from "../../../pages/Common/TopUpScreen";
import { useUserProfileGate } from "../../../hooks/useUserProfileGate";
import { checkMicPermission } from "../../../utils";
import { storageService } from "../../../services/storageService";

import "./index.scss";

export const useAppDispatch: () => AppDispatch = useDispatch;

interface CallsRightSectionProps {
  selectedNumberToCall: any;
}

const CallsRightSection: React.FC<CallsRightSectionProps> = ({
  selectedNumberToCall,
}) => {
  const { gate, modal } = useUserProfileGate();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const isSupportAgent = storageService.getIsSupportAgent();
  const { data: balanceData } = useGetBalanceInfoQuery();
  // const { registered } = useSelector((state: RootState) => state.sip);
  const { setSettingsCurrentScreen } = useSettingSidebarContext();
  const { data } = useGetPhoneNumbersQuery();
  const [dialedNumber, setDialedNumber] = useState("");
  const [addonErrorModalOpen, setAddonErrorModalOpen] = useState(false);
  const [dropdownItems, setDropdownItems] = useState<MenuProps["items"]>([]);
  const { callerId: callerIdValue } = useSelector(
    (state: RootState) => state.sip,
  );
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { calls, currentCallId } = useSelector((state: any) => state.sip);
  const [currentScreen, setCurrentScreen] = useState<"calls" | "top-up">(
    "calls",
  );
  const [checkingAddOn, setCheckingAddOn] = useState(false);
  const [openContactsDropdown, setOpenContactsDropdown] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [getClientRates, { data: ratesData, isLoading: ratesLoading }] =
    useGetClientRatesMutation();
  const [isNumberRegistered, { data: regData, isLoading: regLoading }] =
    useIsNumberRegisteredMutation();

  const handleCall = async (callType: string) => {
    if (!dialedNumber || calls.length || currentCallId) return;

    if (!isOnline) {
      toast.warning("No Internnet Connection. Please check your network.");
      return;
    }

    const hasMicPermission = await checkMicPermission();
    if (!hasMicPermission) return;

    if (callType === "2") {
      if (!socket) {
        setAddonErrorModalOpen(true);
        return;
      }

      if (checkingAddOn) return;
      setCheckingAddOn(true);

      socket.emit("is-call-country-addon-exists", {
        destNumber: dialedNumber,
      });

      const onReply = (message: any) => {
        clearTimeout(timeoutId);
        setCheckingAddOn(false);

        const exists = message.callStatus ? true : false;

        if (exists) {
          dispatch(
            makeCall({
              source: callerIdValue || "",
              target: dialedNumber,
              callType,
            }),
          );
        } else {
          setAddonErrorModalOpen(true);
        }
      };

      socket.once("is-call-country-addon-exists", onReply);

      const timeoutId = setTimeout(() => {
        setCheckingAddOn(false);
        socket.off("is-call-country-addon-exists", onReply);
        setAddonErrorModalOpen(true);
      }, 5000);

      return;
    }

    dispatch(
      makeCall({
        source: callerIdValue || "",
        target: dialedNumber,
        callType,
      }),
    );
  };

  const handleAddBalance = async () => {
    try {
      const res = await gate();
      if (res) {
        setCurrentScreen("top-up");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFavouriteClicked = (favourite: any) => {
    if (!favourite) return;
    setDialedNumber(favourite.contactNumber);
  };

  const debouncedCheckNumber = useCallback(
    debounce((number: string) => {
      isNumberRegistered({ phoneNumber: number });
    }, 1000),
    [isNumberRegistered],
  );

  const debouncedClientRates = useCallback(
    debounce((number: string) => {
      const formattedNumber = number.startsWith("+") ? number : `+${number}`;
      getClientRates({ destNumber: formattedNumber });
    }, 1000),
    [getClientRates],
  );

  const filteredContacts = contacts.filter((contact: any) => {
    const number = contact?.phoneNumber?.toString() || "";
    const name = contact?.fullName?.toLowerCase() || "";
    const search = dialedNumber.toLowerCase();

    if (!search) return true;

    return number.includes(search) || name.includes(search);
  });

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (data && data.data.length > 0) {
      const phoneNumbers = data.data;
      const updatedItems = phoneNumbers.map((item: any) => ({
        key: item.phoneNumber,
        label: item.phoneNumber,
      }));
      setDropdownItems(updatedItems);
    }
  }, [data, callerIdValue]);

  useEffect(() => {
    if (selectedNumberToCall) {
      setDialedNumber(selectedNumberToCall.destinationNumber);
    }
  }, [selectedNumberToCall]);

  useEffect(() => {
    if (dialedNumber.length > 6 && !(regLoading || ratesLoading)) {
      debouncedCheckNumber(dialedNumber);
      debouncedClientRates(dialedNumber);
    }

    return () => {
      debouncedCheckNumber.cancel();
      debouncedClientRates.cancel();
    };
  }, [dialedNumber, debouncedCheckNumber, debouncedClientRates]);

  // const isConnected = registered && isOnline;

  return (
    <div className="calls-right-section-container">
      {currentScreen === "calls" && !isSupportAgent && (
        <div className="balance-container">
          {balanceData && balanceData?.status === 200 && (
            <div className="balance-amount">
              ${" "}
              {balanceData?.data?.balance > 0 ? balanceData?.data?.balance : 0}
            </div>
          )}

          <Button
            type="primary"
            className="add-balance-btn"
            icon={<PlusCircleFilled />}
            onClick={handleAddBalance}
          >
            Add Balance
          </Button>
        </div>
      )}

      {currentScreen === "calls" && (
        <div className="width-wrapper">
          <Space style={{ width: "100%" }}>
            <div className="caller-id">
              Your caller ID displays as
              <Dropdown
                overlayClassName="custom-dropdown"
                trigger={["click"]}
                menu={{
                  items: dropdownItems,
                  onClick: (e) => dispatch(setCallerId(e.key)),
                }}
              >
                <Flex gap={4} className="cursor-pointer">
                  <span className="id">
                    {callerIdValue || "Select a number"}
                  </span>
                  <DownOutlined className="down-arrow" />
                </Flex>
              </Dropdown>
              {/* <p className={`${isConnected ? "text-green" : "text-red"}`}>
                {isConnected ? "connected" : "not connected"}
              </p> */}
            </div>
          </Space>

          <div className="search-input-container">
            <SearchInput
              value={dialedNumber}
              onChange={setDialedNumber}
              onFocus={() => setOpenContactsDropdown(true)}
              onBlur={() => setOpenContactsDropdown(false)}
              placeholder="Type a name or number..."
            />

            {openContactsDropdown && filteredContacts.length > 0 && (
              <div className="contacts-list">
                <List
                  size="small"
                  dataSource={filteredContacts}
                  renderItem={(item: any) => (
                    <List.Item
                      onMouseDown={() => setDialedNumber(item.phoneNumber)}
                      style={{ cursor: "pointer", padding: "6px 12px" }}
                    >
                      <ContactCard
                        phoneNumber={item.phoneNumber}
                        userName={item.fullName}
                        isEfone={item.iseFone}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>

          <Favourites handleClick={handleFavouriteClicked} />

          <Dialer
            onKeyClick={(key) => {
              setDialedNumber((prev) => prev + key);
            }}
          />

          <div className="call-btns-container">
            {regLoading ? (
              <CustomLoader />
            ) : (
              <>
                {regData?.isRegistered && (
                  <Button
                    onClick={() => handleCall("1")}
                    className="call-btn efone-call"
                    type="primary"
                  >
                    eFone Call
                    <br />
                    <span className="sub-text">Free</span>
                  </Button>
                )}
                <Button
                  className="call-btn non-efone-call"
                  onClick={() => handleCall("2")}
                >
                  Call <br />{" "}
                  {ratesData?.rate && (
                    <span className="sub-text">${ratesData.rate} /min</span>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {currentScreen === "top-up" && (
        <TopUpScreen
          showHeader={false}
          handleBackToPrevious={() => setCurrentScreen("calls")}
        />
      )}

      <Modal
        centered
        className="no-addon-modal custom-modal"
        width={400}
        open={addonErrorModalOpen}
        onCancel={() => setAddonErrorModalOpen(false)}
        footer={false}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <div className="custom-cross-icon">
          <img src="/assets/icons/rejected.svg" alt="rejected" />
        </div>

        <h2>Can't Make Call</h2>

        <p>
          You don't have calling add-ons. To purchase add-on click the button
          below
        </p>

        <div className="modal-footer">
          <Button
            type="primary"
            className="modal-ok-btn"
            onClick={() => {
              setSettingsCurrentScreen("subscription");
              navigate("/settings");
            }}
          >
            Subscribe
          </Button>
        </div>
      </Modal>

      {modal}
    </div>
  );
};

export default CallsRightSection;
